import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronRight, ChevronUp, RefreshCw, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { toneClass } from '@/lib/status';
import { Button } from '@/components/ui/button';
import { IconTile, edgeFadeStyle, useScrollEdges } from './ui';

export const BRIEF_FOLLOW_UP = 'Tell me more about this morning’s brief: what should I deal with first, and who owns each item?';

const NO_LAYOUT = { key: '', cut: 0, clamp: 0, collapsible: false };

/**
 * Where each line of the list ends (px from the list's top) and how tall the card is around the list.
 * Every line must be rendered (unclamped) when this runs.
 */
function measureLines(card, list) {
  const top = list.getBoundingClientRect().top - list.scrollTop;
  const bottoms = Array.from(list.children, (row) => row.getBoundingClientRect().bottom - top);
  const chrome = card.getBoundingClientRect().height - list.getBoundingClientRect().height;
  return { bottoms, chrome };
}

/**
 * Morning brief (25 idea 9): the 3–4 things that need a decision today — late LPOs, stages awaiting approval,
 * unpaid invoices, projects past handover — built from live data each morning (GET /ai/morning-brief, only what
 * this role may see). Each line puts a follow-up question in the box; nothing is sent until the person presses
 * Send. "Ask a follow-up" asks about the brief as a whole; "+N not shown" counts the facts the server left out.
 *
 * `fit` (beside the chat): the card never shows half a line. With `maxHeight` (the most it may take so History
 * keeps about four chats) it shows only the whole lines that fit — at least the first — and a "Show N more"
 * toggle for the rest. Opened, it may grow to `openMaxHeight`; if the lines still do not fit, the list is cut at
 * a whole line (at least two) and scrolls, with a fade at the edge that hides lines.
 */
export default function MorningBriefCard({ onAsk, className, fit = false, maxHeight = 0, openMaxHeight = 0 }) {
  const [state, setState] = useState({ status: 'loading', brief: null, error: '' });
  const [expanded, setExpanded] = useState(false);
  const [fontsTick, setFontsTick] = useState(0);
  const [layout, setLayout] = useState(NO_LAYOUT);
  const cardRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const load = useCallback(async ({ refresh = false } = {}) => {
    setState((current) => ({ ...current, status: current.brief ? 'refreshing' : 'loading', error: '' }));
    try {
      const response = await api.get('/ai/morning-brief', refresh ? { params: { refresh: true } } : undefined);
      setState({ status: 'ready', brief: response.data || null, error: '' });
    } catch (err) {
      setState((current) => ({ status: 'error', brief: current.brief, error: getErrorMessage(err, 'Could not load the brief') }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Line heights depend on the web font: measure again once it has loaded.
  useEffect(() => {
    let live = true;
    document.fonts?.ready?.then(() => { if (live) setFontsTick((tick) => tick + 1); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const { brief } = state;
  const items = Array.isArray(brief?.items) ? brief.items : [];
  const when = brief?.generated_at ? formatDateTime(brief.generated_at, { relativeDay: true }) : '';
  const more = Number(brief?.more) || 0;

  // Sizing to whole lines needs a room to fit in (0 in tests and before the column is laid out).
  const sizing = fit && maxHeight > 0 && items.length > 0 && state.status !== 'loading';
  const itemsKey = items.map((item) => `${item.key}\u0001${item.title}\u0001${item.sub || ''}`).join('\u0002');
  const layoutKey = sizing ? [expanded, maxHeight, openMaxHeight, fontsTick, itemsKey].join('|') : '';
  const measured = sizing && layout.key === layoutKey;
  const remeasuring = sizing && !measured;
  // While a new measurement is taken the toggle keeps its last state (that render is never painted), so it keeps
  // the keyboard focus when it is pressed.
  const collapsible = sizing && layout.collapsible;
  const collapsed = collapsible && !expanded;
  const shown = collapsed && !remeasuring ? items.slice(0, layout.cut) : items;
  const hiddenCount = Math.max(0, items.length - layout.cut);
  const clamp = collapsible && expanded && !remeasuring ? layout.clamp : 0;

  // Not measured yet for this room / these lines → every line is rendered unclamped; measure before the paint
  // and render again with the whole lines that fit.
  useLayoutEffect(() => {
    if (!remeasuring || !cardRef.current || !listRef.current) return;
    const { bottoms, chrome } = measureLines(cardRef.current, listRef.current);
    const fitting = (room) => bottoms.filter((bottom) => chrome + bottom <= room + 0.5).length;
    const cut = Math.max(1, fitting(maxHeight)); // the first line always shows, even when it alone is too tall
    const fitsOpen = Math.max(Math.min(2, bottoms.length), fitting(Math.max(openMaxHeight, maxHeight)));
    setLayout({
      key: layoutKey,
      cut,
      clamp: fitsOpen < bottoms.length ? Math.ceil(bottoms[fitsOpen - 1]) : 0,
      collapsible: cut < bottoms.length,
    });
  }, [remeasuring, layoutKey, maxHeight, openMaxHeight]);

  const edges = useScrollEdges(listRef, { axis: 'y', watch: `${state.status}|${shown.length}|${clamp}|${collapsed}` });
  const listStyle = fit ? { maxHeight: clamp || undefined, ...edgeFadeStyle(edges, 18, 'y') } : undefined;

  return (
    <section
      ref={cardRef}
      className={cn('rounded-card border border-line bg-surface px-4 shadow-card', fit ? 'flex min-h-0 flex-col py-3' : 'py-4', className)}
      aria-labelledby="morning-brief-title"
      data-testid="ai-morning-brief"
      data-collapsed={collapsible ? String(collapsed) : undefined}
    >
      <div className="flex shrink-0 items-center gap-2.5">
        <IconTile icon={Sparkles} tone="lime" size={28} />
        <h2 id="morning-brief-title" className="text-[14px] font-bold text-ink">Morning brief</h2>
        <span className="ml-auto flex items-center gap-1">
          {when ? <span className="text-[12px] text-ink-3" title="Built from live data">{when}</span> : null}
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="text-ink-3"
            aria-label="Rebuild the brief from live data"
            title="Rebuild from live data"
            onClick={() => load({ refresh: true })}
            disabled={state.status === 'loading' || state.status === 'refreshing'}
            data-testid="ai-brief-refresh"
          >
            <RefreshCw className={cn(state.status === 'refreshing' && 'animate-spin')} aria-hidden="true" />
          </Button>
        </span>
      </div>

      {state.status === 'loading' ? (
        <div className="mt-3 space-y-3" aria-label="Loading the brief">
          {[0, 1, 2].map((index) => (
            <div key={index} className="space-y-1.5">
              <div className="ui-skeleton-shimmer h-3 w-3/4 rounded bg-surface-2" />
              <div className="ui-skeleton-shimmer h-2.5 w-1/2 rounded bg-surface-2" />
            </div>
          ))}
        </div>
      ) : state.status === 'error' && !brief ? (
        <p className="mt-3 text-[12.5px] text-ink-3">
          {state.error}{' '}
          <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => load()}>Try again</button>
        </p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-[12.5px] leading-snug text-ink-3" data-testid="ai-brief-empty">
          Nothing needs a decision this morning — no late LPOs, waiting approvals or unpaid invoices in what you can see.
        </p>
      ) : (
        <ul
          ref={listRef}
          id={listId}
          className={cn('-mx-2 space-y-0.5', fit ? 'mt-1.5 min-h-0 overflow-y-auto overscroll-contain px-0.5' : 'mt-2')}
          style={listStyle}
          data-testid="ai-brief-list"
          data-scroll-end={fit && edges.end ? 'true' : undefined}
        >
          {shown.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => onAsk(item.question)}
                className={cn(
                  'group/brief flex w-full items-start gap-2.5 rounded-[10px] px-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  fit ? 'py-1.5' : 'py-2'
                )}
                title={`Ask: ${item.question}`}
                data-testid={`ai-brief-item-${item.key}`}
              >
                <span className={cn('ui-tone-dot mt-[5px] h-[7px] w-[7px] shrink-0', toneClass(item.tone))} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold leading-snug text-ink">{item.title}</span>
                  {/* Beside the chat each line is one row (the whole text is in the tooltip), so History keeps its room. */}
                  {item.sub ? (
                    <span className={cn('mt-0.5 block text-[12px] leading-snug text-ink-3', fit && 'truncate')} title={fit ? item.sub : undefined}>
                      {item.sub}
                    </span>
                  ) : null}
                </span>
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-4 transition-colors group-hover/brief:text-brand-ink" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length ? (
        <div
          className={cn('flex shrink-0 items-center justify-between gap-2 border-t border-line-2 text-[12.5px]', fit ? 'mt-1.5 pt-2' : 'mt-2 pt-2.5')}
          data-testid="ai-brief-footer"
        >
          <button
            type="button"
            onClick={() => onAsk(BRIEF_FOLLOW_UP)}
            className="inline-flex min-h-5 shrink-0 items-center gap-1 font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="ai-brief-follow-up"
          >
            Ask a follow-up
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <span className="flex min-w-0 items-center gap-1">
            {/* Facts the server left out of the brief (not lines folded away here) — only once every line is shown. */}
            {more > 0 && !collapsed ? (
              <span
                className="truncate text-[12px] text-ink-3"
                title={`${more === 1 ? '1 more fact' : `${more} more facts`} than fit in the brief — ask a follow-up to hear ${more === 1 ? 'it' : 'them'}`}
                data-testid="ai-brief-more"
              >
                +{more} not shown
              </span>
            ) : null}
            {collapsible ? (
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                aria-controls={listId}
                aria-label={expanded ? 'Show fewer lines' : undefined}
                title={expanded ? 'Show fewer lines' : `Show the other ${hiddenCount === 1 ? 'line' : `${hiddenCount} lines`}`}
                className={cn(
                  'inline-flex shrink-0 items-center justify-center gap-0.5 rounded-md font-semibold text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  expanded ? 'h-5 w-6' : 'h-5 px-1.5'
                )}
                data-testid="ai-brief-toggle"
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <>
                    Show {hiddenCount} more
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  </>
                )}
              </button>
            ) : null}
          </span>
        </div>
      ) : null}
    </section>
  );
}
